// apps/prompts/src/components/admin/AdminRoute.tsx
import { ApiError, useAuthStore } from '@neuroforge/shared/client'
import { LoadingSpinner } from '@neuroforge/ui'
import { useQuery } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation } from 'react-router'

import { adminSessionKey, fetchAdminSession, isVerifiedAdmin } from '../../lib/admin-session'

/** L'API (JWT + DB) et non le seul store client décide de la permission ADMIN. */
export default function AdminRoute() {
  const { isLoading, isAuthenticated, user, accessToken } = useAuthStore()
  const location = useLocation()
  const returnPath = `${location.pathname}${location.search}${location.hash}`
  const loginPath = `/auth/login?redirect=${encodeURIComponent(returnPath)}`

  const check = useQuery({
    queryKey: adminSessionKey(user?.id, accessToken),
    enabled: !isLoading && isAuthenticated && !!accessToken && user?.role === 'ADMIN',
    queryFn: ({ signal }) => fetchAdminSession(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: false, // 401/403 : ne pas multiplier les requêtes d'auth.
  })

  if (isLoading) return <AdminLoading />
  if (!isAuthenticated || !accessToken) return <Navigate to={loginPath} replace />
  if (user?.role !== 'ADMIN') return <Navigate to="/" replace />

  // Au montage/retour focus, ne pas afficher un écran admin avant la réponse
  // serveur, y compris si l'utilisateur a été déclassé ou la session révoquée.
  if (check.isPending || check.isFetching) return <AdminLoading />
  if (check.error instanceof ApiError && check.error.status === 403) {
    return <Navigate to="/" replace />
  }
  if (check.isError) return <Navigate to={loginPath} replace />
  if (!isVerifiedAdmin(check.data, user.id)) return <Navigate to="/" replace />

  return <Outlet />
}

function AdminLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center" aria-busy="true">
      <LoadingSpinner size="lg" />
    </div>
  )
}
